import React, { useState } from 'react';
import {
  X, AlertTriangle, Calendar, Clock, MapPin,
  RefreshCw, User, CheckCircle
} from 'lucide-react';
import { logger } from '../utils/logger';

const CancellationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  meeting,
  userType,
  currentUser
}) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationCategory, setCancellationCategory] = useState('scheduling_conflict');
  const [allowReschedule, setAllowReschedule] = useState(true);
  const [suggestReschedule, setSuggestReschedule] = useState(false);
  const [suggestedDates, setSuggestedDates] = useState(['', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !meeting) return null;

  const cancellationCategories = [
    { value: 'scheduling_conflict', label: 'Scheduling Conflict', icon: '📅' },
    { value: 'emergency', label: 'Emergency', icon: '🚨' },
    { value: 'illness', label: 'Illness', icon: '🏥' },
    { value: 'travel', label: 'Travel Issues', icon: '✈️' },
    { value: 'weather', label: 'Weather/External Factors', icon: '🌧️' },
    { value: 'personal', label: 'Personal Reasons', icon: '👤' },
    { value: 'other', label: 'Other', icon: '📝' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const userName = currentUser.name ||
                       (currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : null) ||
                       (currentUser.first_name && currentUser.last_name ? `${currentUser.first_name} ${currentUser.last_name}` : null) ||
                       currentUser.email ||
                       'Unknown User';

      const cancellationData = {
        cancelledBy: currentUser.id,
        cancelledByName: userName,
        cancelledByType: userType,
        reason: cancellationReason,
        category: cancellationCategory,
        allowReschedule,
        suggestReschedule: suggestReschedule && suggestedDates.some(date => date.trim()),
        suggestedDates: suggestedDates.filter(date => date.trim())
      };

      await onConfirm(cancellationData);
    } catch (error) {
      logger.error('Error cancelling meeting:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUrgentCancellation = () => {
    try {
      const meetingDate = new Date(`${meeting.date} ${meeting.time}`);
      const now = new Date();
      const hoursUntilMeeting = (meetingDate - now) / (1000 * 60 * 60);
      return hoursUntilMeeting <= 24 && hoursUntilMeeting > 0;
    } catch (error) {
      return false;
    }
  };

  const handleSuggestedDateChange = (index, value) => {
    const newDates = [...suggestedDates];
    newDates[index] = value;
    setSuggestedDates(newDates);
  };

  const urgentCancellation = isUrgentCancellation();

  // Get the other party's name - handle both string and object formats
  const otherPartyName = (() => {
    if (userType === 'sales') {
      // For sales reps, get the customer name
      if (typeof meeting.customer === 'string') {
        return meeting.customer;
      } else if (meeting.customer && typeof meeting.customer === 'object') {
        return `${meeting.customer.first_name || ''} ${meeting.customer.last_name || ''}`.trim() || meeting.customer_name || 'Healthcare Provider';
      }
      return meeting.customer_name || 'Healthcare Provider';
    } else {
      // For providers, get the sales rep name
      return meeting.salesRepName || meeting.sales_rep_name || 'Sales Representative';
    }
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel Meeting</h3>
              <p className="text-sm text-gray-500">
                {urgentCancellation ? '⚠️ Last-minute cancellation' : 'Meeting cancellation'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Meeting Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Meeting Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>{meeting.date}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <span>{meeting.time}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{meeting.restaurant_name || meeting.restaurant?.name || meeting.restaurant || 'TBD'}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center space-x-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm">
              Meeting with <strong>{otherPartyName}</strong>
            </span>
          </div>
        </div>

        {/* Cancellation Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Urgency Warning */}
          {urgentCancellation && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium">Last-minute cancellation</p>
                  <p className="text-amber-700 text-sm mt-1">
                    This meeting is within 24 hours. Please provide a detailed reason as this may impact future scheduling.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cancellation Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Reason for cancellation
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {cancellationCategories.map((category) => (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => setCancellationCategory(category.value)}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    cancellationCategory === category.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{category.icon}</span>
                    <span className="text-sm font-medium">{category.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detailed Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional details {urgentCancellation && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="Please provide additional context for the cancellation..."
              required={urgentCancellation}
            />
          </div>

          {/* Reschedule Options */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="allowReschedule"
                checked={allowReschedule}
                onChange={(e) => setAllowReschedule(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="allowReschedule" className="text-sm font-medium text-gray-700">
                Allow the other party to request rescheduling
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="suggestReschedule"
                checked={suggestReschedule}
                onChange={(e) => setSuggestReschedule(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="suggestReschedule" className="text-sm font-medium text-gray-700">
                Suggest alternative dates
              </label>
            </div>

            {suggestReschedule && (
              <div className="ml-7 space-y-3">
                <p className="text-sm text-gray-600">Suggest up to 3 alternative dates:</p>
                {suggestedDates.map((date, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500 w-12">#{index + 1}</span>
                    <input
                      type="datetime-local"
                      value={date}
                      onChange={(e) => handleSuggestedDateChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Impact Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-blue-800 font-medium">What happens next:</p>
                <ul className="text-blue-700 text-sm mt-1 space-y-1">
                  <li>• {otherPartyName} will be notified immediately</li>
                  <li>• The meeting will be marked as cancelled</li>
                  {allowReschedule && <li>• They can request to reschedule if desired</li>}
                  {suggestReschedule && suggestedDates.some(date => date.trim()) && (
                    <li>• Your suggested dates will be shared with them</li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Keep Meeting
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (urgentCancellation && !cancellationReason.trim())}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Cancelling...</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  <span>Cancel Meeting</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancellationDialog;